import React from 'react'
import { ArticleModel } from '@/types/api'

/**
 * Follows the backend's Google product feed: only stock on hand is in stock, while an article
 * on a purchase order or held by a supplier is a backorder. Google flags a page whose
 * availability disagrees with the feed.
 */
const schemaAvailability = (article: ArticleModel) => {
  if (article.quantityAvailable > 0) return 'https://schema.org/InStock'
  if (article.estimatedDeliveryDate || article.quantityExternal > 0) return 'https://schema.org/BackOrder'
  return 'https://schema.org/OutOfStock'
}

/**
 * Product structured data for the product page.
 *
 * Returns the script element itself rather than a component, because next/head only keeps
 * elements that are its direct children: a component nested inside it survives the server
 * render and is then dropped by the client head manager on hydration, so Google, which reads
 * the rendered page, never sees it.
 *
 * Returns nothing for an article without a price. Google reads a price of 0 as a missing
 * price, so an offer is better left out than published as free.
 *
 * Returns nothing for an article without an image either. Google requires an image on a
 * product and reports every product page without one as invalid, and a placeholder would be
 * flagged as a generic image instead.
 */
export const productJsonLd = (article: ArticleModel, url: string): React.ReactElement | null => {
  if (!article.price || !article.imageId) return null

  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: `${article.brandName} ${article.articleText}`,
    description: article.articleDetails?.description || article.articleText,
    sku: article.id.toString(),
    image: `${process.env.NEXT_PUBLIC_API}/images/${article.imageId}`,
    brand: {
      '@type': 'Brand',
      name: article.brandName,
    },
    offers: {
      '@type': 'Offer',
      url,
      priceCurrency: article.currencyCode,
      price: article.price,
      availability: schemaAvailability(article),
      seller: {
        '@type': 'Organization',
        name: 'StatusFalgar',
      },
    },
  }

  return (
    <script
      key="product-jsonld"
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  )
}
